// HomePage.js
import React, { useEffect, useContext, useRef } from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import MiningSection from '../components/MiningSection';
import ShopSection from '../components/ShopSection';
import InviteFriends from '../components/InviteFriends';
import DailyTasks from '../components/DailyTasks';
import Navigation from '../components/Navigation';
import RegistrationStatus from '../components/RegistrationStatus';
import { TelegramUserContext } from '../App';
import { useUserData } from '../contexts/UserDataContext';

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  background-color: #ffffff;
  height: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  font-size: 13px;
  overflow: hidden; /* Prevent whole page from scrolling */
`;

const ScrollableContent = styled.div`
  flex: 1;
  min-height: 0; /* Fix for flexbox overflow issues */
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  position: relative;
  overscroll-behavior: none;
  -webkit-overscroll-behavior: none;
  touch-action: pan-y;
  padding-bottom: calc(61px + var(--tg-safe-area-inset-bottom, 0px)); /* Space for fixed navigation */
`;

const FooterWrapper = styled.div`
  width: 100%;
  background-color: white;
  max-width: 100%;
  flex-shrink: 0;
  box-sizing: border-box;
  /* Safe area for bottom navigation on iOS */
  padding-bottom: var(--tg-safe-area-inset-bottom, 0px);
`;

const HomePage = ({ onNavigate, isAppInitialized }) => { // Changed prop
  const telegramUser = useContext(TelegramUserContext);
  // Removed initialLoadUserData from destructuring as it's no longer called directly here
  const { userData, isLoading: isUserDataLoading } = useUserData();
  
  const shopRef = useRef(null);
  const scrollableContentRef = useRef(null);

  const handlePrimaryCoinClick = () => {
    if (shopRef.current && scrollableContentRef.current) {
      // Get the position of the shop section relative to the scrollable container
      const shopRect = shopRef.current.getBoundingClientRect();
      const contentRect = scrollableContentRef.current.getBoundingClientRect();
      
      // Calculate the exact scroll position needed
      const scrollPosition = shopRect.top - contentRect.top + scrollableContentRef.current.scrollTop - 20;
      
      // Scroll the content container to the calculated position
      scrollableContentRef.current.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    }
  };

  // Effect to potentially trigger a data load or refresh if isAppInitialized is true,
  // but primarily, UserDataContext should be populated by initialData from App.js.
  // This effect acts as a fallback or for specific HomePage logic dependent on full app initialization.
  useEffect(() => {
    if (isAppInitialized && telegramUser?.id) {
      // UserDataContext is now populated via initialData prop from App.js.
      // This effect can be used if HomePage specifically needs to react once the app is initialized
      // and basic user data is available. For example, to trigger animations or page-specific logic.
      // console.log('[HomePage] App is initialized and telegramUser ID is available.');
    }
    // Removed initialLoadUserData from dependencies as it's no longer called.
    // userData is included to re-run if it changes, though direct data changes are handled by context.
  }, [isAppInitialized, telegramUser?.id, userData]);

  // Example of showing a loading indicator based on context's isLoading state
  if (isUserDataLoading && !userData) { // Show loading only if context is loading and no data yet
    return (
      <Container style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div>Loading App Data...</div>
      </Container>
    );
  }

  return (
    <Container>
      <Header 
        secondaryCoinBalance={userData?.balance || 0} 
        primaryCoinBalance={userData?.primary_coin_balance || 0} 
        onPrimaryCoinClick={handlePrimaryCoinClick}
      />
      <ScrollableContent ref={scrollableContentRef}>
        <MiningSection 
          initialUserData={userData}
        />
        <InviteFriends />
        <DailyTasks />
        <div ref={shopRef}>
          <ShopSection />
        </div>
        <RegistrationStatus />
      </ScrollableContent>
      <FooterWrapper>
        <Navigation activePage="home" onNavigate={onNavigate} />
      </FooterWrapper>
    </Container>
  );
};

export default HomePage;
